import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api'

export interface Message {
  id: number
  match_id: number
  sender_id: number
  content: string
  content_translated: string | null
  original_locale: string
  culture_hint?: string | null
  is_read: number
  read_at?: string | null
  sent_by: 'user' | 'ai_avatar' | 'offline_responder' | 'persona' | 'system'
  created_at: string
}

export interface MatchInfo {
  id: number
  match_score: number
  other_user: {
    id: number
    username: string
    picture: string | null
    locale: string
    is_online?: number
    has_persona?: number
  }
  is_ai_chatting: boolean
  stage?: {
    current_stage: number
    stage_name: string
    message_count: number
    message_limit: number | null
    stage_expires_at: string | null
  }
}

interface UseChatMessagesOptions {
  matchId: string | undefined
  userId: number | undefined
  onStageLockedError?: (error: any) => void
  onMessageBlockedError?: (error: any) => void
}

interface UseChatMessagesReturn {
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  matchInfo: MatchInfo | null
  loading: boolean
  sending: boolean
  sendingMessage: string
  isOtherUserTyping: boolean
  input: string
  setInput: (value: string) => void
  handleInputChange: (value: string) => void
  handleSend: () => Promise<void>
  fetchMessagesQuietly: () => Promise<void>
}

export function useChatMessages({
  matchId,
  userId,
  onStageLockedError,
  onMessageBlockedError,
}: UseChatMessagesOptions): UseChatMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([])
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [sendingMessage, setSendingMessage] = useState('')
  const [input, setInput] = useState('')
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false)
  const [waitingForResponse, setWaitingForResponse] = useState(false)

  const pollingIntervalRef = useRef<number | null>(null)
  const messagesRef = useRef<Message[]>([])
  const typingDebounceRef = useRef<number | null>(null)
  const typingTimeoutRef = useRef<number | null>(null)
  const responseTimeoutRef = useRef<number | null>(null)

  // Keep messagesRef in sync
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const fetchMessages = useCallback(async () => {
    if (!matchId) return
    try {
      setLoading(true)
      const data = await api.matches.getMessages(parseInt(matchId))
      setMessages((data.messages || []) as Message[])
      setMatchInfo((data.match || null) as MatchInfo | null)
    } catch (error) {
      // Silent fail for initial load
    } finally {
      setLoading(false)
    }
  }, [matchId])

  const fetchMessagesQuietly = useCallback(async () => {
    if (!matchId) return
    try {
      const data = await api.matches.getMessages(parseInt(matchId))
      const newMessages = (data.messages || []) as Message[]

      if (newMessages.length !== messagesRef.current.length) {
        setMessages(newMessages)
        setMatchInfo((data.match || null) as MatchInfo | null)

        if (waitingForResponse && newMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1]
          if (lastMessage.sender_id !== userId) {
            setWaitingForResponse(false)
            setIsOtherUserTyping(false)
            if (responseTimeoutRef.current) {
              clearTimeout(responseTimeoutRef.current)
              responseTimeoutRef.current = null
            }
          }
        }
      }

      if (data.match) {
        const match = data.match as any
        const isUser1 = match.user_id_1 === userId
        const otherUserTyping = isUser1 ? match.user2_typing : match.user1_typing
        setIsOtherUserTyping(otherUserTyping === 1)
      }
    } catch {
      // Silent fail for polling
    }
  }, [matchId, userId, waitingForResponse])

  // Initial fetch
  useEffect(() => {
    if (matchId) {
      fetchMessages()
    }
  }, [matchId, fetchMessages])

  // Polling
  useEffect(() => {
    if (!matchId) return

    pollingIntervalRef.current = window.setInterval(() => {
      fetchMessagesQuietly()
    }, 5000)

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [matchId, fetchMessagesQuietly])

  // Visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      } else {
        fetchMessagesQuietly()
        if (!pollingIntervalRef.current && matchId) {
          pollingIntervalRef.current = window.setInterval(() => {
            fetchMessagesQuietly()
          }, 2000)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [matchId, fetchMessagesQuietly])

  const handleInputChange = useCallback((value: string) => {
    setInput(value)

    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current)
    }

    if (value.trim().length > 0 && matchId) {
      typingDebounceRef.current = window.setTimeout(() => {
        api.matches.setTyping(parseInt(matchId), true).catch(() => {})

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }
        typingTimeoutRef.current = window.setTimeout(() => {
          api.matches.setTyping(parseInt(matchId), false).catch(() => {})
        }, 3000)
      }, 1000)
    } else if (matchId) {
      api.matches.setTyping(parseInt(matchId), false).catch(() => {})
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [matchId])

  const handleSend = useCallback(async () => {
    if (!input.trim() || sending || !matchId) return

    // Clear typing indicators
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current)
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }
    api.matches.setTyping(parseInt(matchId), false).catch(() => {})

    const messageContent = input.trim()
    setSending(true)
    setSendingMessage(messageContent)
    setInput('')

    // Re-focus input
    setTimeout(() => {
      const inputEl = document.querySelector('input[type="text"]') as HTMLInputElement
      inputEl?.focus()
    }, 10)

    try {
      await api.matches.sendMessage(parseInt(matchId), messageContent)

      const shouldShowTyping = matchInfo?.other_user?.has_persona === 1 || matchInfo?.is_ai_chatting
      if (shouldShowTyping) {
        setWaitingForResponse(true)
        setIsOtherUserTyping(true)

        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current)
        }
        responseTimeoutRef.current = window.setTimeout(() => {
          setWaitingForResponse(false)
          setIsOtherUserTyping(false)
        }, 30000)
      }

      await fetchMessagesQuietly()
    } catch (error: any) {
      if (error.error === 'Message blocked') {
        setInput(messageContent)
        onMessageBlockedError?.(error)
        return
      }

      if (error.stage_locked) {
        setInput(messageContent)
        onStageLockedError?.(error)
        return
      }

      setInput(messageContent)
      throw error
    } finally {
      setSending(false)
      setSendingMessage('')
    }
  }, [input, sending, matchId, matchInfo, fetchMessagesQuietly, onStageLockedError, onMessageBlockedError])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current)
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current)
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current)
    }
  }, [])

  return {
    messages,
    setMessages,
    matchInfo,
    loading,
    sending,
    sendingMessage,
    isOtherUserTyping,
    input,
    setInput,
    handleInputChange,
    handleSend,
    fetchMessagesQuietly,
  }
}
